"use client";

/**
 * Course Offering Planner — chair tool to plan which courses are offered each
 * semester and assign instructors per section. Built on top of the curriculum
 * course catalog: the chair picks a target year + semester, then for each
 * course in the curriculum adds/edits/removes sections (instructor + section
 * number). Saving writes back to `course.sections[]` in Firestore.
 *
 * Data flow:
 *   curriculum (70 courses)
 *       ↓
 *   chair picks year/semester (e.g. 1/2569)
 *       ↓
 *   for each course → list sections matching that semester
 *       ↓
 *   chair edits → save → updates course.sections[] (preserving other-semester sections)
 *       ↓
 *   instructors see only their assigned courses for THAT semester in "รายวิชาของฉัน"
 */
import { useEffect, useMemo, useState } from "react";
import {
  useProgramCourses, useUsersList, useReplaceCourseSections,
  normalizeSemesterId,
  type CourseLite,
} from "@/lib/firebase/queries";

interface Props {
  programId: string;
}

// Semester labels — covers ภาค 1, ภาค 2, ภาคฤดูร้อน
const SEMESTERS: Array<{ key: string; label: string }> = [
  { key: "1", label: "ภาค 1" },
  { key: "2", label: "ภาค 2" },
  { key: "S", label: "ภาคฤดูร้อน" },
];

function currentBE(): number {
  // Thai academic year:
  //   ก.ค.(7)-ธ.ค.(12) of CE yyyy → academic year yyyy+543
  //   ม.ค.(1)-มิ.ย.(6) of CE yyyy → academic year yyyy+542
  const d = new Date();
  const m = d.getMonth() + 1;
  return m >= 7 ? d.getFullYear() + 543 : d.getFullYear() + 542;
}

export function OfferingPlanner({ programId }: Props) {
  const courses = useProgramCourses(programId);
  const users = useUsersList();
  const replace = useReplaceCourseSections();

  const thisBE = currentBE();
  const [year, setYear] = useState<number>(thisBE);
  const [semester, setSemester] = useState<string>("1");
  const [search, setSearch] = useState("");
  const [savingCourseId, setSavingCourseId] = useState<string | null>(null);
  const [userPicked, setUserPicked] = useState(false); // true เมื่อผู้ใช้เลือกปี/ภาคเอง

  // หาปี/ภาคล่าสุดที่ "เปิดแผนไว้แล้ว" (มี section จริง) เพื่อ default ไปที่นั่น
  const latestOffering = useMemo(() => {
    let maxYear = 0;
    const counts: Record<number, Record<string, number>> = {};
    for (const c of courses.data ?? []) {
      for (const s of (c.sections ?? [])) {
        if (s.cancelled || !s.semester) continue;
        const m = String(s.semester).match(/^(\d+|S)\/(\d{2})$/i);
        if (!m) continue;
        const yy = 2500 + parseInt(m[2], 10);
        const sem = m[1];
        counts[yy] = counts[yy] || {};
        counts[yy][sem] = (counts[yy][sem] || 0) + 1;
        if (yy > maxYear) maxYear = yy;
      }
    }
    if (maxYear === 0) return null;
    let bestSem = "1", bestCount = -1;
    for (const [sem, cnt] of Object.entries(counts[maxYear])) {
      if (cnt > bestCount) { bestCount = cnt; bestSem = sem; }
    }
    return { year: maxYear, semester: bestSem };
  }, [courses.data]);

  // Default ไปปี/ภาคล่าสุดที่เปิดแผนไว้ (ครั้งแรกที่ courses โหลด) — ถ้าผู้ใช้ยังไม่เลือกเอง
  useEffect(() => {
    if (userPicked || !courses.data) return;
    if (latestOffering) {
      setYear(latestOffering.year);
      setSemester(latestOffering.semester);
    }
  }, [courses.data, latestOffering, userPicked]);

  // The "tag" stored in section.semester — we use the short form "1/68" so it
  // round-trips through normalizeSemesterId() to the canonical "1-2568".
  const yearShort = String(year).slice(-2);
  const semTag = `${semester}/${yearShort}`;

  // Year options: 4 past + current + 4 future
  const yearOptions = useMemo(() => {
    const arr: number[] = [];
    for (let y = thisBE - 4; y <= thisBE + 4; y++) arr.push(y);
    return arr;
  }, [thisBE]);

  // Filtered course list — exclude legacy/old-curriculum courses (current = 2567)
  const filteredCourses = useMemo(() => {
    const s = search.trim().toLowerCase();
    return (courses.data ?? []).filter(c => {
      // Skip legacy courses (category="legacy" or name says หลักสูตรเก่า)
      if ((c.category ?? "").toLowerCase() === "legacy") return false;
      if ((c.nameTh ?? "").includes("หลักสูตรเก่า")) return false;
      if (!s) return true;
      const code = (c.code ?? "").toLowerCase();
      const nameTh = (c.nameTh ?? "").toLowerCase();
      const nameEn = ((c as { nameEn?: string }).nameEn ?? "").toLowerCase();
      return code.includes(s) || nameTh.includes(s) || nameEn.includes(s);
    });
  }, [courses.data, search]);

  // Summary for the selected semester (across whole curriculum)
  const summary = useMemo(() => {
    let courseCount = 0;
    let sectionCount = 0;
    const instructors = new Set<string>();
    for (const c of courses.data ?? []) {
      const here = (c.sections ?? []).filter(s => s.semester === semTag && !s.cancelled);
      if (here.length > 0) {
        courseCount++;
        sectionCount += here.length;
        for (const s of here) if (s.instructorUid) instructors.add(s.instructorUid);
      }
    }
    return { courseCount, sectionCount, instructorCount: instructors.size };
  }, [courses.data, semTag]);

  return (
    <section className="rounded-2xl border-2 border-purple-300 bg-purple-50/30 p-5">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-purple-800 flex items-center gap-2">
            📅 แผนเปิดสอนรายภาคเรียน
          </h2>
          <p className="text-xs text-slate-600 mt-0.5">
            เลือกปี/ภาคเรียน แล้วกำหนดว่าวิชาใดเปิดบ้าง + assign อาจารย์ผู้สอนต่อ section
          </p>
        </div>
        <div className="text-right">
          <div className="text-[11px] text-slate-500">ภาคนี้เปิด</div>
          <div className="text-sm font-bold text-purple-700">
            {summary.courseCount} รายวิชา · {summary.sectionCount} sections · {summary.instructorCount} อาจารย์
          </div>
        </div>
      </div>

      {/* Year / Semester selector */}
      <div className="bg-white rounded-lg border p-3 flex items-center gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2 text-sm">
          <label className="text-slate-600">ปีการศึกษา:</label>
          <select
            value={year}
            onChange={(e) => { setUserPicked(true); setYear(parseInt(e.target.value)); }}
            className="border border-slate-300 rounded-md px-2 py-1 text-sm bg-white"
          >
            {yearOptions.map(y => (
              <option key={y} value={y}>
                {y}{latestOffering?.year === y ? " (เปิดแผนแล้ว)" : y === thisBE ? " (ปีปฏิทิน)" : ""}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <label className="text-slate-600">ภาคเรียน:</label>
          <div className="flex gap-1">
            {SEMESTERS.map(s => (
              <button
                key={s.key}
                onClick={() => { setUserPicked(true); setSemester(s.key); }}
                className={
                  "text-xs px-3 py-1 rounded-md border transition " +
                  (semester === s.key
                    ? "bg-purple-500 text-white border-purple-500"
                    : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50")
                }
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div className="ml-auto text-[11px] text-slate-500 font-mono">
          tag: <strong className="text-slate-700">{semTag}</strong>
          {" → "}
          <strong className="text-slate-700">{normalizeSemesterId(semTag)}</strong>
        </div>
      </div>

      {/* Search filter */}
      <div className="mb-3">
        <input
          type="search"
          placeholder="🔍 กรองรายวิชา (รหัส หรือ ชื่อ)..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full text-sm border border-slate-300 rounded-md px-3 py-1.5 bg-white"
        />
      </div>

      {/* Course list with editable sections for the selected semester */}
      <div className="space-y-2">
        {courses.isLoading && <div className="text-sm text-slate-500 py-4 text-center">กำลังโหลด...</div>}
        {!courses.isLoading && filteredCourses.length === 0 && (
          <div className="rounded-lg border-2 border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
            {(courses.data?.length ?? 0) === 0
              ? "ยังไม่มีรายวิชาในระบบ — Approve เล่มหลักสูตรเพื่อ sync รายวิชา"
              : "ไม่พบรายวิชาที่ตรงเงื่อนไข"}
          </div>
        )}
        {filteredCourses.map(c => (
          <CourseOfferingRow
            key={c.id}
            programId={programId}
            course={c}
            semTag={semTag}
            users={users.data ?? []}
            isSaving={savingCourseId === c.id || (savingCourseId === null && replace.isPending)}
            onSave={async (newSections) => {
              setSavingCourseId(c.id);
              try {
                await replace.mutateAsync({ programId, courseId: c.id, sections: newSections });
              } finally {
                setSavingCourseId(null);
              }
            }}
          />
        ))}
      </div>
    </section>
  );
}

// ============ Row: 1 course + sections for the selected semester ============

interface SectionDraft {
  section: number;
  instructorUid: string;
  instructorName: string;
  semester: string;
  cancelled?: boolean;
  cancelledReason?: string;
}

function CourseOfferingRow({
  programId: _programId,
  course,
  semTag,
  users,
  isSaving,
  onSave,
}: {
  programId: string;
  course: CourseLite;
  semTag: string;
  users: Array<{ uid: string; displayName: string; email: string }>;
  isSaving: boolean;
  onSave: (sections: SectionDraft[]) => Promise<void> | void;
}) {
  // Sections in OTHER semesters (preserved as-is)
  const otherSections = useMemo(
    () => (course.sections ?? []).filter(s => s.semester !== semTag) as SectionDraft[],
    [course.sections, semTag]
  );
  // Sections in THIS semester — editable
  const initialMine = useMemo(
    () => (course.sections ?? []).filter(s => s.semester === semTag) as SectionDraft[],
    [course.sections, semTag]
  );
  const [draft, setDraft] = useState<SectionDraft[]>(initialMine);
  // Re-sync when course data refreshes or semester changes
  const [pinned, setPinned] = useState(`${course.id}|${semTag}|${initialMine.length}`);
  const current = `${course.id}|${semTag}|${initialMine.length}`;
  if (current !== pinned) {
    setPinned(current);
    setDraft(initialMine);
  }

  const dirty = useMemo(() => {
    if (draft.length !== initialMine.length) return true;
    return draft.some((d, i) => {
      const o = initialMine[i];
      return !o
        || d.section !== o.section
        || d.instructorUid !== o.instructorUid
        || d.cancelled !== o.cancelled;
    });
  }, [draft, initialMine]);

  const [expanded, setExpanded] = useState(initialMine.length > 0);

  function nextSectionNumber(): number {
    if (draft.length === 0) return 1;
    return Math.max(...draft.map(s => s.section)) + 1;
  }

  function addSection() {
    setDraft(d => [
      ...d,
      { section: nextSectionNumber(), instructorUid: "", instructorName: "", semester: semTag },
    ]);
    setExpanded(true);
  }

  function updateSection(idx: number, patch: Partial<SectionDraft>) {
    setDraft(d => d.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  }

  function removeSection(idx: number) {
    setDraft(d => d.filter((_, i) => i !== idx));
  }

  async function handleSave() {
    const merged = [...otherSections, ...draft].sort((a, b) => a.section - b.section);
    await onSave(merged);
  }

  function handleReset() {
    setDraft(initialMine);
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200">
      <div className="px-3 py-2 flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setExpanded(e => !e)}
          className="text-xs text-slate-400 hover:text-slate-700 w-6 text-center"
          title={expanded ? "ยุบ" : "ขยาย"}
        >
          {expanded ? "▼" : "▶"}
        </button>
        <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">{course.code}</span>
        <span className="text-sm font-medium truncate flex-1 min-w-0" title={course.nameTh}>{course.nameTh}</span>
        <span className="text-[11px] px-2 py-0.5 rounded bg-purple-50 text-purple-700">
          {draft.length} section{draft.length !== 1 ? "s" : ""}
        </span>
        {otherSections.length > 0 && (
          <span className="text-[10px] text-slate-400" title={`มี ${otherSections.length} sections อยู่ในภาคอื่น`}>
            (+ {otherSections.length} ภาคอื่น)
          </span>
        )}
        {dirty && <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">● ยังไม่บันทึก</span>}
      </div>

      {expanded && (
        <div className="border-t border-slate-100 p-3 space-y-2">
          {draft.length === 0 ? (
            <div className="text-[12px] text-slate-500 italic py-2">
              ยังไม่ได้เปิดสอนในภาคนี้ — คลิก &ldquo;+ เพิ่ม section&rdquo; เพื่อกำหนด
            </div>
          ) : (
            <div className="space-y-1">
              {draft.map((s, idx) => (
                <div key={idx} className="flex items-center gap-2 flex-wrap text-sm">
                  <input
                    type="number"
                    min="1"
                    value={s.section}
                    onChange={(e) => updateSection(idx, { section: parseInt(e.target.value) || 1 })}
                    className="w-16 border border-slate-300 rounded px-2 py-1 text-xs text-center"
                    title="หมายเลข section"
                  />
                  <select
                    value={s.instructorUid}
                    onChange={(e) => {
                      const u = users.find(x => x.uid === e.target.value);
                      updateSection(idx, {
                        instructorUid: e.target.value,
                        instructorName: u?.displayName ?? "",
                      });
                    }}
                    className={
                      "flex-1 min-w-[200px] border border-slate-300 rounded px-2 py-1 text-xs " +
                      (s.instructorUid ? "bg-white" : "bg-amber-50 border-amber-300")
                    }
                  >
                    <option value="">— เลือกอาจารย์ —</option>
                    {users.map(u => (
                      <option key={u.uid} value={u.uid}>
                        {u.displayName} ({u.email})
                      </option>
                    ))}
                  </select>
                  {s.cancelled && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded" title={s.cancelledReason ?? ""}>
                      ✕ ยกเลิก
                    </span>
                  )}
                  <button
                    onClick={() => removeSection(idx)}
                    className="text-xs text-red-500 hover:text-red-700 hover:underline px-1"
                    title="ลบ section นี้ออกจากแผน"
                  >
                    ลบ
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
            <button
              onClick={addSection}
              className="text-xs rounded-md border border-purple-300 bg-white hover:bg-purple-50 text-purple-700 px-3 py-1"
            >
              + เพิ่ม section
            </button>
            {dirty && (
              <>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="ml-auto text-xs rounded-md bg-green-600 hover:bg-green-700 text-white px-3 py-1 disabled:opacity-50"
                >
                  {isSaving ? "..." : "💾 บันทึก"}
                </button>
                <button
                  onClick={handleReset}
                  disabled={isSaving}
                  className="text-xs rounded-md border border-slate-300 bg-white hover:bg-slate-50 px-2 py-1 disabled:opacity-50"
                >
                  ยกเลิก
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
